import { Data } from "@core/content/types";
import {
  configureCreatorContentStore,
  getCreatorPayload,
  initializeCreatorData as initializeCreatorDataBase,
  ContentData,
} from "./contentStore";
import {
  extractArtworkInputsFromPayload,
  generateImageTargets,
  type ImageTargetDescriptor,
} from "@webcore/core/targets/target.image.8w";
import mswamuralImage from "@assets/images/mswamural-image.webp";

// Cache for generated image targets (populated lazily)
let generatedImageTargets: ImageTargetDescriptor[] | null = null;
let imageTargetsPromise: Promise<ImageTargetDescriptor[]> | null = null;

export {
  MicrositeData,
  INITIAL_CONTENT_ID,
  getContent,
} from "./contentStore";
export { ContentData } from "./contentStore";

const getImageTargetSourcePayload = () => {
  if (typeof window !== "undefined" && (window as any).__ARTWORK_DATA__) {
    return (window as any).__ARTWORK_DATA__;
  }

  return getCreatorPayload();
};

// Set this once per project:
// - Local file: new URL("./creatorData.json", import.meta.url).href
// - URL:        "https://launch.eyejack.io/Collection-... ?j=1"
export const CREATOR_DATA_SOURCE =
  "https://launch.eyejack.io/Artwork-4f72487a-47cd-466c-afc2-e80350b0627a/json";

// Manual content for non-creator items (scenes, models, etc.)
// Use targetName to match the 8th Wall target and override creator data (e.g. add custom scale)
export const MANUAL_CONTENT: Data[] = [];

configureCreatorContentStore({
  creatorDataSource: CREATOR_DATA_SOURCE,
  manualContent: MANUAL_CONTENT,
});

type ArtworkImageOverride = Data & {
  icon?: string;
  cover?: string;
  constructedCover?: string;
  images?: Record<string, { path: string }>;
};

const applyArtworkImageOverrides = (content: Data[]) => {
  content.forEach((item) => {
    if (item.type !== "image") return;

    const artworkItem = item as ArtworkImageOverride;
    artworkItem.icon = mswamuralImage;
    artworkItem.cover = mswamuralImage;
    artworkItem.constructedCover = mswamuralImage;
    artworkItem.images = {
      ...artworkItem.images,
      artwork512: { path: mswamuralImage },
      artwork1024: { path: mswamuralImage },
    };
  });
};

export const initializeCreatorData = async (): Promise<void> => {
  await initializeCreatorDataBase();
  applyArtworkImageOverrides(ContentData);
};

/**
 * Get image target data with client-side image generation (async version).
 * Generates target images in the browser using Canvas API.
 */
export const getImageTargetDataAsync = async (): Promise<ImageTargetDescriptor[]> => {
  // Check if pre-built targets are injected
  if ((window as any).__IMAGE_TARGET_DATA__?.length > 0) {
    return (window as any).__IMAGE_TARGET_DATA__;
  }

  // Check if already generated
  if (generatedImageTargets) {
    return generatedImageTargets;
  }

  // Return existing promise if generation is in progress
  if (imageTargetsPromise) {
    return imageTargetsPromise;
  }

  // Generate client-side using Canvas API
  const artworkPayload = getImageTargetSourcePayload();
  const artworkInputs = extractArtworkInputsFromPayload(artworkPayload);
  imageTargetsPromise = generateImageTargets(artworkInputs).then((targets) => {
    generatedImageTargets = targets;
    return targets;
  });

  return imageTargetsPromise;
};
